import React, { useState, useEffect } from "react";
import clsx from "clsx";
import { useFormChangeHandler } from "hooks/useFormChangeHandler";
import { useSubmitHandler } from "hooks/useSubmitHandler";
import { useCreateData, useUpdateData } from "hooks/useApiHooks";
import { formatNumber, unformatNumber } from "utils/utils";
import {
  saveCashierLocation,
  saveCashierSessionData,
} from "utils/locationUtils";

const OpenCasirPage = ({ initialData, onSuccess, onError }) => {
  const API_URL = "/cashier-session/opened";
  // const { products, refetch } = useProduct();
  console.log("Initial Data", initialData);

  // Menambahkan state untuk menyimpan products
  const [formData, setFormData] = useState({
    balance: "",
  });

  const isUpdateMode = !!initialData; // Cek apakah ini update mode atau create mode

  // API hook untuk create dan update
  const { createData: createProduct } = useCreateData(
    API_URL,
    "cashier-session/openeds"
  );
  const { updateData: updateProduct } = useUpdateData(
    `${API_URL}/${initialData?.id}`,
    "cashier-session/openeds"
  );

  // Gunakan hook untuk menangani perubahan form
  const handleChange = useFormChangeHandler(setFormData);

  // Gunakan hook untuk menangani pengiriman form
  const { handleSubmit, isSubmitting, formError } = useSubmitHandler(
    isUpdateMode ? updateProduct : createProduct,
    onSuccess,
    onError
  );

  const handleBeforeSubmit = (e) => {
    e.preventDefault();
    const payload = {
      ...formData,
      balance: unformatNumber(formData.balance), // Unformat balance value before sending
    };

    // Simpan location dan session data ke localStorage saat open cashier
    if (formData.location) {
      saveCashierLocation(formData.location);
      // Simpan session data untuk location ini
      saveCashierSessionData(formData.location, {
        status: "opening",
        location: formData.location,
        balance: payload.balance,
        opened_at: new Date().toISOString(),
      });
    }

    handleSubmit(payload, e);
  };

  useEffect(() => {
    // Jika initialData tersedia, isi form untuk update
    if (initialData) {
      setFormData({
        ...initialData,
      });
    }
  }, [initialData]);

  // Format value when focus is lost
  const handleBlur = () => {
    if (formData.balance) {
      const formattedValue = formatNumber(formData.balance); // Format using utils
      setFormData({
        ...formData,
        balance: formattedValue,
      });
    }
  };

  // Convert formatted value back to number on focus
  const handleFocus = () => {
    const rawValue = unformatNumber(formData.balance); // Unformat using utils
    setFormData({
      ...formData,
      balance: rawValue,
    });
  };

  // Handler khusus untuk input balance agar selalu terformat ribuan saat diketik
  const handleBalanceChange = (e) => {
    const { value } = e.target;
    // Hanya angka
    const number = value.replace(/[^\d]/g, "");
    // Format ribuan
    const formatted = number.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    setFormData((prev) => ({ ...prev, balance: formatted }));
  };

  const locationsOptions = [
    { value: "bar", label: "Bar" },
    { value: "coffee_shop", label: "Coffee Shop" },
    { value: "merchandise", label: "Merchandise" },
  ];

  return (
    <form
      onSubmit={(e) => handleBeforeSubmit(e)}
      className="d-flex flex-column"
      style={{ height: "100%" }}
    >
      <div className="action-sheet-content flex-grow-1">
        {/* Select location as 3 card choices with icon */}
        <div className="row mb-3 align-items-center">
          <label
            htmlFor="location"
            className="col-sm-4 col-form-label d-flex justify-content-end"
            style={{ fontSize: "14px" }}
          >
            Location:
          </label>
          <div className="col-sm-8">
            <div className="d-flex gap-2">
              {locationsOptions.map((option) => (
                <div
                  key={option.value}
                  className={clsx(
                    "card p-2 flex-grow-1 text-center cursor-pointer",
                    formData.location === option.value && "border-primary"
                  )}
                  style={{
                    border:
                      formData.location === option.value
                        ? "2px solid #0d6efd"
                        : "1px solid #dee2e6",
                    boxShadow: "none",
                    minWidth: "90px",
                  }}
                  onClick={() =>
                    handleChange({
                      target: {
                        name: "location",
                        value: option.value,
                      },
                    })
                  }
                >
                  {/* Example icons, replace with your own or use a library like react-icons */}
                  <div style={{ fontSize: "24px", marginBottom: "8px" }}>
                    {option.value === "bar" && (
                      <span role="img" aria-label="Bar">
                        🍸
                      </span>
                    )}
                    {option.value === "coffee_shop" && (
                      <span role="img" aria-label="Coffee">
                        ☕
                      </span>
                    )}
                    {option.value === "merchandise" && (
                      <span role="img" aria-label="Merchandise">
                        🛍️
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: "14px" }}>{option.label}</div>
                </div>
              ))}
            </div>
            {formError?.location && (
              <div className="invalid-feedback d-block">
                {formError.location}
              </div>
            )}
          </div>
        </div>
        {/* balance */}
        <div className="row mb-3 align-items-center">
          <label
            htmlFor="name"
            className="col-sm-4 col-form-label d-flex justify-content-end"
            style={{ fontSize: "14px" }}
          >
            Balance:
          </label>
          <div className="col-sm-8">
            <input
              type="text"
              className={clsx(
                "form-control gotra-form-control rounded-1",
                formError?.balance && "is-invalid"
              )}
              id="balance"
              placeholder=" balance"
              name="balance"
              value={formData.balance}
              onChange={handleBalanceChange}
              // onFocus={handleFocus} // Convert back to number when focused
              // onBlur={handleBlur} // Format when focus is lost
              disabled={isSubmitting}
              style={{ fontSize: "14px", height: "35px" }}
              required
              minLength={3}
            />
            {formError?.balance && (
              <div className="invalid-feedback">{formError.nabalanceme}</div>
            )}
          </div>
        </div>
      </div>

      {/* Submit Button */}
      <div className="form-group mt-auto d-grid">
        <button
          type="submit"
          className="btn btn-primary btn-block"
          disabled={isSubmitting}
        >
          {isUpdateMode ? "Update" : "Balance"}
        </button>
      </div>
    </form>
  );
};

export default OpenCasirPage;
